<app-configuration-page
  title="Staff Management"
  description="Invite, organize, and keep every instructor’s access up to date."
  [breadcrumbs]="[{ label: 'Staff Management' }]">

  <ng-container configPageActions>
    <button *ngIf="_permissionsService.canAccess('staff_manage')" mat-flat-button color="primary" type="button" (click)="openStaffDialog(frmManageStaffComponent, null, 'create')">
      <mat-icon>person_add</mat-icon>
      Add staff
    </button>
  </ng-container>

  <nav mat-tab-nav-bar configPageTabs #tabPanel>
    <a mat-tab-link
      *ngFor="let link of links"
      [routerLink]="link.path"
      [queryParams]="link.queryParams"
      routerLinkActive #rla="routerLinkActive"
      [active]="link === activeLink || rla.isActive"
      (click)="activeLink = link">
      {{link.label}}
    </a>
  </nav>

  <!-- One surface: the filter toolbar sits on the top edge of the table card
       rather than floating in a separate card above it. -->
  <mat-card class="table-card">
    <mat-card-content>
      <div class="filter-grid d-flex align-items-center flex-wrap gap-2 px-3 py-2">
        <span class="filter-count text-muted small flex-grow-1">
          {{ dataSource?.filteredData?.length || 0 }} of {{ metrics.total | number }} people
        </span>

        <!-- Search sits at the toolbar's right edge and expands on demand. -->
        <div class="filter-actions d-flex align-items-center ms-auto">
          <button mat-icon-button
                  type="button"
                  aria-label="Refresh"
                  matTooltip="Refresh"
                  [disabled]="ShowProgressBar"
                  (click)="refreshView()">
            <mat-icon>refresh</mat-icon>
          </button>

          <button *ngIf="!searchOpen"
                  mat-icon-button
                  type="button"
                  aria-label="Search"
                  matTooltip="Search"
                  (click)="openSearch()">
            <mat-icon>search</mat-icon>
          </button>

          <mat-form-field *ngIf="searchOpen" class="filter-search" appearance="outline">
            <input matInput
                #quickSearchInput
                [value]="tableSearchValue"
                (input)="applyQuickFilter($event.target.value)"
                placeholder="Search staff">
            <mat-icon matPrefix>search</mat-icon>
            <button matSuffix
                    mat-icon-button
                    type="button"
                    aria-label="Close search"
                    (click)="closeSearch()">
              <mat-icon>close</mat-icon>
            </button>
          </mat-form-field>
        </div>
      </div>

      <mat-progress-bar [class.invisible]="!ShowProgressBar" mode="indeterminate"></mat-progress-bar>

      <div class="table-empty-state" *ngIf="!ShowProgressBar && !hasResults">
        <mat-icon>group_off</mat-icon>
        <h3>No staff in this view</h3>
        <p>Invite your team or toggle filters to see existing members.</p>
        <button *ngIf="_permissionsService.canAccess('staff_manage')" mat-raised-button color="primary" type="button" (click)="openStaffDialog(frmManageStaffComponent, null, 'create')">
          Add staff member
        </button>
      </div>

      <div class="table-responsive-sticky table-responsive" *ngIf="hasResults">
        <table mat-table #displayTable [dataSource]="dataSource"
          cdkDropList [cdkDropListData]="dataSource?.data" (cdkDropListDropped)="drop($event)" [cdkDropListDisabled]="!_permissionsService.canAccess('staff_manage')"
          matSort
          (matSortChange)="sortDataView($event)"
          class="staff-table"
          appGridTable gridTableKey="staff-management">

          <ng-container matColumnDef="name">
            <th mat-header-cell *matHeaderCellDef mat-sort-header sortActionDescription="Sort by staff name">
              Staff Member
            </th>
            <td mat-cell *matCellDef="let element">
              <div class="staff-cell">
                <div class="avatar-circle" aria-hidden="true">{{ initials(element) }}</div>
                <div>
                  <div class="staff-name">{{element.field_first_name | htmlDecode}} {{element.field_last_name | htmlDecode}}</div>
                  <div class="staff-username text-muted" *ngIf="usernameFromEmail(element)">@{{ usernameFromEmail(element) }}</div>
                </div>
              </div>
            </td>
          </ng-container>

          <ng-container matColumnDef="contact">
            <th mat-header-cell *matHeaderCellDef>
              Contact
            </th>
            <td mat-cell *matCellDef="let element">
              <div class="contact-cell">
                <a *ngIf="element.mail" [href]="'mailto:' + element.mail">{{element.mail}}</a>
                <span class="text-muted" *ngIf="!element.mail">No email</span>
                <span class="contact-phone" *ngIf="element.field_cell_number">{{element.field_cell_number | phone}}</span>
              </div>
            </td>
          </ng-container>

          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef mat-sort-header sortActionDescription="Sort by status">
              Status
            </th>
            <td mat-cell *matCellDef="let element">
              <mat-chip-list aria-label="Staff status">
                <mat-chip [color]="statusChipColor(element)" selected>
                  {{ statusLabel(element) }}
                </mat-chip>
              </mat-chip-list>
            </td>
          </ng-container>

          <ng-container matColumnDef="activity">
            <th mat-header-cell *matHeaderCellDef>
              Last Active
            </th>
            <td mat-cell *matCellDef="let element">
              <div class="activity-cell">
                <span>{{element.access || '-'}}</span>
              </div>
            </td>
          </ng-container>

          <ng-container matColumnDef="created">
            <th mat-header-cell *matHeaderCellDef mat-sort-header sortActionDescription="Sort by created date">
              Created
            </th>
            <td mat-cell *matCellDef="let element">{{element.created | date: 'MM/dd/yyyy'}}</td>
          </ng-container>

          <ng-container matColumnDef="roles">
            <th mat-header-cell *matHeaderCellDef>
              Role(s)
            </th>
            <td mat-cell *matCellDef="let element">
              <mat-chip-list *ngIf="rolesList(element).length > 0" aria-label="Roles assigned">
                <mat-chip *ngFor="let role of rolesList(element)" color="primary" selected>
                  {{ role }}
                </mat-chip>
              </mat-chip-list>
              <span class="text-muted" *ngIf="rolesList(element).length === 0">-</span>
            </td>
          </ng-container>

          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef>
              <span *ngIf="_permissionsService.canAccess('staff_manage')">Actions</span>
            </th>
            <td mat-cell *matCellDef="let element">
              <button *ngIf="_permissionsService.canAccess('staff_manage')" mat-icon-button [matMenuTriggerFor]="staffMenu" [matMenuTriggerData]="{ uid: element.uid }" aria-label="Staff actions">
                <mat-icon>more_vert</mat-icon>
              </button>
            </td>
          </ng-container>

          <mat-menu #staffMenu="matMenu">
            <ng-template matMenuContent let-uid="uid">
                <button mat-menu-item
                    (click)="openStaffDialog(frmManageStaffComponent, uid, 'edit')">
                    Edit Staff Member
                </button>
            </ng-template>
          </mat-menu>

          <ng-container matColumnDef="pagination-footer">
            <td mat-footer-cell *matFooterCellDef [attr.colspan]="displayedColumns.length">
              <mat-paginator (page)="pageChanged($event)"
                [pageSizeOptions]="onPageSizeOptions()"
                [disabled]="ShowProgressBar"
                aria-label="Select page of view"></mat-paginator>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
          <tr cdkDrag [cdkDragDisabled]="!_permissionsService.canAccess('staff_manage')" mat-row *matRowDef="let row; columns: displayedColumns; trackBy: trackByUid"></tr>
          <tr mat-footer-row *matFooterRowDef="['pagination-footer']; sticky: true"></tr>
        </table>
      </div>
    </mat-card-content>
  </mat-card>

</app-configuration-page>
